import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userAPI, UserUsage } from '../services/user';

interface CostEstimateProps {
  documentLength: number;
  mode: 'quick' | 'deep';
}

export default function CostEstimate({ documentLength, mode }: CostEstimateProps) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [usage, setUsage] = useState<UserUsage | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    userAPI.getUsage().then(setUsage).catch(() => {});
  }, [isAuthenticated]);

  if (!isAuthenticated || !usage) return null;

  const remaining = Math.max(0, usage.analysesLimit - usage.analysesCount);
  const hasCredits = usage.creditsBalance > 0;
  const totalAvailable = remaining + usage.creditsBalance;
  const isExhausted = totalAvailable === 0;
  const isLow = totalAvailable <= 1;

  // Estimate complexity based on document length and mode
  const getComplexityLevel = (): 'low' | 'medium' | 'high' => {
    if (mode === 'deep') return documentLength > 5000 ? 'high' : 'medium';
    return documentLength > 10000 ? 'medium' : 'low';
  };
  const complexity = getComplexityLevel();

  return (
    <div className={`mt-4 p-3 rounded-lg border text-sm ${
      isExhausted
        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
        : isLow
        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
        : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'
    }`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <svg className={`w-4 h-4 flex-shrink-0 ${
            isExhausted ? 'text-red-500' : isLow ? 'text-amber-500' : 'text-slate-400 dark:text-slate-500'
          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <div>
            <span className={`font-medium ${
              isExhausted ? 'text-red-700 dark:text-red-400' : isLow ? 'text-amber-700 dark:text-amber-400' : 'text-slate-700 dark:text-slate-300'
            }`}>
              {t('costEstimate.cost')}: 1 {t('costEstimate.analysis')}
            </span>
            <span className="text-slate-400 dark:text-slate-500 mx-2">|</span>
            <span className={`${
              isExhausted ? 'text-red-600 dark:text-red-400' : isLow ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500 dark:text-slate-400'
            }`}>
              {t('costEstimate.remaining', { count: remaining })}
              {hasCredits && (
                <span className="text-blue-600 dark:text-blue-400"> +{usage.creditsBalance} {t('costEstimate.credits')}</span>
              )}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            complexity === 'high'
              ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
              : complexity === 'medium'
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
              : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
          }`}>
            {t(`costEstimate.complexity.${complexity}`)}
          </span>
        </div>
      </div>

      {isExhausted && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">
          {t('costEstimate.exhausted')}{' '}
          <Link to="/pricing" className="underline hover:no-underline font-medium">
            {t('costEstimate.upgrade')}
          </Link>
        </p>
      )}
    </div>
  );
}
